import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_V2 } from '../../components/onboarding/onboardingV2Routes';
import { AnalisisEnCurso } from '../../components/onboarding/marca/AnalisisEnCurso';
import { FondoCordillera } from '../../components/onboarding/marca/FondoCordillera';
import { GuiaCumbre, useDialogo, type Gesto } from '../../components/onboarding/marca/GuiaCumbre';
import { PreguntasDeEspera } from '../../components/onboarding/marca/PreguntasDeEspera';
import { BotonDeSeccion, SeccionDeMarca } from '../../components/onboarding/marca/SeccionDeMarca';
import { dialogoDelTablero } from '../../lib/onboardingMarca/dialogo';
import { pasoTrasLaMarca } from '../../lib/onboardingMarca/canales';
import { supabase } from '../../lib/supabase';
import { ensureDefaultBrand } from '../../lib/ensureDefaultBrand';
import { confirmarMarcaNueva, DESCRIBIR_MARCA, MARCA_NUEVA } from '../../lib/onboardingMarca/marcaNueva';
import { prepararElementosDeLaMarca } from '../v2/elementosIniciales';
import { enGuion } from '../../lib/onboardingMarca/guion';
import { dominioDe } from '../../lib/onboardingMarca/link';
import {
  ESCANEO_VACIO,
  iniciarEscaneoManual,
  leerEscaneo,
  type ColorDeMarca,
  type EscaneoDeMarca,
} from '../../lib/onboardingMarca/escaneo';
import { MARCA_POR_DEFECTO } from '../../lib/altaDesdeCuestionario';
import {
  estadoDelTablero,
  etiquetaDelPaso,
  pasosDelOrigen,
  pasosDisponibles,
  pasosPendientes,
  porQueNoSigue,
  preguntasSinResponder,
  primerasOraciones,
  puedeSeguir,
  seccionesDelAnalisis,
  siguienteRevelado,
  type OrigenDeLaMarca,
  type PasoId,
} from '../../lib/onboardingMarca/tablero';
import {
  confirmarMarca,
  leerMarca,
  RUBROS,
  subirLogo,
  type MarcaGuardada,
  type RespuestasDeLaEspera,
} from '../../lib/onboardingMarca/marca';
import { guardarEstado, leerEstado } from '../../lib/onboardingMarca/estado';
import { RUBRO_OTRO, rubroFinal, rubroQueCorresponde } from '../../lib/onboardingMarca/rubroOtro';

/**
 * A2 + A3 · El tablero de marca (ADR-2026-09-28-onboarding-v2-etapa-1).
 *
 * Mientras el escaneo corre, las tarjetas pasan de esqueleto a contenido de a una (el
 * director de ritmo de `tablero.ts`) y arriba se responden las preguntas de la espera.
 * Cuando termina, la MISMA pantalla es la revisión: cada tarjeta se corrige en su
 * lugar y "Todo bien, sigamos" confirma la marca y saca a la empresa del onboarding
 * pendiente. Las preguntas son obligatorias y no se mueven de arriba; el botón y el
 * estado del análisis viven en la barra de abajo (ADR-2026-09-29-onboarding-v2-accion-abajo).
 *
 * Sin link ("Ahora no") es el MISMO proceso a mano (ADR-2026-09-29-onboarding-v2-camino-
 * manual): la persona cuenta nombre, rubro, logo y qué vende; "Armar mi marca" corre la
 * rama manual del mismo análisis, y paleta, tipografía y tono se revelan igual que con un
 * link. De ahí en adelante —revisión, preguntas, barra, confirmación— es la misma pantalla.
 * Si el análisis falla o se demora, las tarjetas quedan para completar.
 *
 * Con Cumbre (ADR-2026-10-02-onboarding-v2-tablero-con-cumbre): arriba guía con un diálogo
 * por estado. Si el análisis YA terminó al llegar (pasa seguido: corrió mientras se
 * respondían las preguntas), se presenta todo de una, en cascada. Si no, sólo hay sección
 * para lo que ya se analizó y lo que falta va junto en `AnalisisEnCurso`.
 */

const POLL_MS = 1_200;
/** Pasado esto sin terminar, se deja seguir con lo que haya. */
const DEMORA_MAX_MS = 150_000;

type Borrador = Omit<MarcaGuardada, 'id'> & { id: string | null };
type Campo = keyof Omit<Borrador, 'id'>;

const BORRADOR_VACIO: Borrador = {
  id: null,
  nombre: '',
  rubro: '',
  colores: [],
  tipografias: {},
  logoUrl: null,
  analisis: '',
};

const ETIQUETA_DE_ROL: Record<string, string> = {
  primary: 'Principal',
  secondary: 'Secundario',
  accent: 'Acento',
  neutral: 'Neutro',
};

/** "VCP Concept | Ropa Streetwear…" → "VCP Concept". */
function nombreDesdeTitulo(titulo: string): string {
  return titulo.split(/\s[|–—-]\s/)[0]?.trim() ?? '';
}

export function TableroDeMarca() {
  const navigate = useNavigate();
  const inicial = useMemo(() => leerEstado(null), []);
  const companyId = inicial?.companyId ?? null;
  /**
   * "Añadir nueva marca" (ADR-2026-10-02-marca-nueva-como-el-onboarding): una marca ADICIONAL.
   * Sin preguntas, sin caer en la default si el análisis falla, y confirmar guarda sólo la marca.
   */
  const nueva = inicial?.flujo === 'marca_nueva';
  /**
   * "Describí tu marca" desde la home (ADR-2026-10-19-describir-la-marca-con-el-onboarding-nuevo):
   * sin preguntas y confirmar guarda sólo la marca, como la nueva, pero la marca es la DEFAULT
   * (el escaneo ya fue a ella) y, si el análisis falla, cae en la default como el onboarding.
   */
  const describir = inicial?.flujo === 'describir_marca';
  const sinPreguntas = nueva || describir;
  const linkDelFlujo = nueva ? MARCA_NUEVA.link : describir ? DESCRIBIR_MARCA.link : ONBOARDING_V2.setup;
  const origen: OrigenDeLaMarca = inicial?.origen ?? (inicial?.escaneoId ? 'web' : 'manual');
  const manual = origen === 'manual';
  const pasos = pasosDelOrigen(origen);
  const ordenPasos = useMemo(() => pasos.map((p) => p.id), [pasos]);
  const [escaneoId, setEscaneoId] = useState<string | null>(inicial?.escaneoId ?? null);
  /** Sin link, antes de "Armar mi marca": la persona está contando lo básico. */
  const contando = manual && !escaneoId;

  const [escaneo, setEscaneo] = useState<EscaneoDeMarca>(ESCANEO_VACIO);
  const [revelados, setRevelados] = useState<PasoId[]>([]);
  const ultimoRevelado = useRef<number | null>(null);
  /** El análisis ya había terminado al llegar: todo se presenta junto, en cascada. */
  const [llegoCompleto, setLlegoCompleto] = useState(false);
  const primeraLectura = useRef(true);
  const [demorado, setDemorado] = useState(false);

  const [borrador, setBorrador] = useState<Borrador>(BORRADOR_VACIO);
  const editados = useRef(new Set<Campo>());
  const [respuestas, setRespuestas] = useState<RespuestasDeLaEspera>(inicial?.respuestas ?? {});
  const [editandoColores, setEditandoColores] = useState(false);
  const [subiendoLogo, setSubiendoLogo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Lo que el sitio le diría al análisis y sin link lo cuenta la persona. */
  const [contanos, setContanos] = useState({ descripcion: '', publico: '' });
  /** Con "Otro": cuál es, en sus palabras (ADR-2026-10-12-el-rubro-otro-se-cuenta-y-se-ajusta). */
  const [rubroDetalle, setRubroDetalle] = useState('');
  const esOtro = borrador.rubro === RUBRO_OTRO;
  // Lo que escribió, si corresponde a un rubro de la lista: se le sugiere y, al confirmar, se usa.
  const rubroSugerido = esOtro ? rubroQueCorresponde(rubroDetalle) : null;
  /** Lo que escribió cuando tocó "Usar ese rubro": el rubro deja de ser "Otro", pero su
   *  descripción sigue valiendo para el agente mientras no elija otro rubro (review #174). */
  const [detalleDelSugerido, setDetalleDelSugerido] = useState<{ rubro: string; detalle: string } | null>(null);
  const detalleParaGuardar = esOtro
    ? rubroDetalle.trim()
    : detalleDelSugerido?.rubro === borrador.rubro
      ? detalleDelSugerido.detalle
      : '';
  const [armando, setArmando] = useState(false);
  const inputLogo = useRef<HTMLInputElement>(null);

  // Sin estado (entró directo a la URL): vuelve al link.
  useEffect(() => {
    if (!companyId) navigate(linkDelFlujo, { replace: true });
  }, [companyId, navigate, linkDelFlujo]);

  /** Carga un dato del análisis sin pisar lo que la persona ya cambió. */
  const completar = useCallback((parcial: Partial<Borrador>) => {
    setBorrador((b) => {
      const nuevo = { ...b };
      for (const [k, v] of Object.entries(parcial) as Array<[keyof Borrador, unknown]>) {
        if (k === 'id') nuevo.id = (v as string | null) ?? b.id;
        else if (!editados.current.has(k) && v !== undefined && v !== null && v !== '') {
          (nuevo as Record<string, unknown>)[k] = v;
        }
      }
      return nuevo;
    });
  }, []);

  const editar = (parcial: Partial<Omit<Borrador, 'id'>>) => {
    for (const k of Object.keys(parcial)) editados.current.add(k as Campo);
    setBorrador((b) => ({ ...b, ...parcial }));
  };

  // ── Poll del escaneo ──
  useEffect(() => {
    if (!escaneoId) return;
    let vivo = true;
    let timer: number | undefined;
    const tick = async () => {
      const r = await leerEscaneo(escaneoId);
      if (!vivo) return;
      if (r) setEscaneo(r);
      // Ya terminado en la primera lectura: no hay nada que revelar de a uno.
      if (primeraLectura.current) {
        primeraLectura.current = false;
        if (r && (r.estado === 'listo' || r.estado === 'fallo')) {
          ultimoRevelado.current = Date.now();
          setRevelados([...ordenPasos]);
          setLlegoCompleto(true);
        }
      }
      if (!r || (r.estado !== 'listo' && r.estado !== 'fallo')) timer = window.setTimeout(tick, POLL_MS);
    };
    void tick();
    const demora = window.setTimeout(() => vivo && setDemorado(true), DEMORA_MAX_MS);
    return () => {
      vivo = false;
      window.clearTimeout(timer);
      window.clearTimeout(demora);
    };
    // `ordenPasos` sólo cambia con el origen, que no cambia en la pantalla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escaneoId]);

  // ── Director de ritmo: revela de a un paso ──
  const disponibles = useMemo(
    () => (demorado ? new Set<PasoId>(ordenPasos) : pasosDisponibles(escaneo, origen)),
    [escaneo, demorado, ordenPasos, origen],
  );
  useEffect(() => {
    if (revelados.length === ordenPasos.length) return;
    const id = window.setInterval(() => {
      const ahora = Date.now();
      setRevelados((prev) => {
        const siguiente = siguienteRevelado({
          disponibles,
          revelados: prev,
          ultimoReveladoMs: ultimoRevelado.current,
          ahoraMs: ahora,
          orden: ordenPasos,
        });
        if (!siguiente) return prev;
        ultimoRevelado.current = ahora;
        return [...prev, siguiente];
      });
    }, 150);
    return () => window.clearInterval(id);
  }, [disponibles, revelados.length, ordenPasos]);

  // ── Los hallazgos entran al borrador a medida que se revelan ──
  const h = escaneo.hallazgos;
  const visto = (p: PasoId) => revelados.includes(p);
  useEffect(() => {
    // Sin link, el nombre y el logo los puso la persona: no se toman del análisis.
    if (!manual && visto('sitio') && h.sitio)
      completar({
        nombre: h.sitio.nombre || nombreDesdeTitulo(h.sitio.titulo),
      });
    if (!manual && visto('logo') && h.logo) completar({ logoUrl: h.logo });
    if (visto('colores') && h.identidad) {
      completar({
        nombre: h.identidad.nombre,
        rubro: RUBROS.includes(h.identidad.rubro) ? h.identidad.rubro : '',
        colores: h.identidad.colores,
        tipografias: h.identidad.tipografias,
        analisis: h.identidad.analisis,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revelados, h.sitio, h.logo, h.identidad, completar]);

  // ── La marca guardada: la del escaneo, o la default si no hubo escaneo o falló ──
  useEffect(() => {
    if (!companyId) return;
    let vivo = true;
    (async () => {
      let brandId = escaneo.marca?.brand_id ?? null;
      // Una marca nueva no cae en la default: si el análisis falla, confirmar la crea.
      if (!brandId && !nueva && (contando || escaneo.estado === 'fallo' || demorado)) {
        // En modo guion no se escribe nada: se lee la marca default que haya, sin crearla.
        if (!enGuion()) await ensureDefaultBrand(companyId);
        const { data } = await supabase
          .from('brand_profiles')
          .select('id')
          .eq('id_company', companyId)
          .eq('is_default', true)
          .limit(1)
          .maybeSingle();
        brandId = (data as { id?: string } | null)?.id ?? null;
      }
      if (!brandId) return;
      const marca = await leerMarca(brandId);
      if (!vivo || !marca) return;
      // "Mi marca" es el nombre de relleno del alta, no uno que haya elegido la persona.
      completar({
        ...marca,
        nombre: marca.nombre === MARCA_POR_DEFECTO ? '' : marca.nombre,
      });
    })();
    return () => {
      vivo = false;
    };
  }, [companyId, contando, escaneo.marca?.brand_id, escaneo.estado, demorado, completar, nueva]);

  // Tipografía: la fuente sugerida, cargada de Google Fonts para mostrarla.
  const fuente = borrador.tipografias.suggested?.find((f) => f.trim())?.trim() ?? null;
  useEffect(() => {
    if (!fuente) return;
    const id = `om-fuente-${fuente.replace(/\s+/g, '-')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fuente.replace(/\s+/g, '+')}:wght@400;700;800&display=swap`;
    document.head.appendChild(link);
  }, [fuente]);

  const responder = (parcial: RespuestasDeLaEspera) => {
    setRespuestas((r) => {
      const nuevas = { ...r, ...parcial };
      if (companyId) guardarEstado({ companyId, respuestas: nuevas });
      return nuevas;
    });
  };

  const alSubirLogo = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('El logo tiene que ser una imagen (PNG o JPG).');
      return;
    }
    setSubiendoLogo(true);
    setError(null);
    try {
      editar({ logoUrl: await subirLogo(file) });
    } catch {
      setError('No pudimos subir el logo. Intenta de nuevo.');
    } finally {
      setSubiendoLogo(false);
    }
  };

  const cambiarColor = (i: number, cambio: Partial<ColorDeMarca>) =>
    editar({
      colores: borrador.colores.map((c, j) => (j === i ? { ...c, ...cambio } : c)),
    });

  // Las preguntas también son obligatorias antes de "Armar mi marca", no sólo antes de seguir
  // (ADR-2026-09-29-onboarding-v2-accion-abajo).
  const preguntasQueFaltan = sinPreguntas ? 0 : preguntasSinResponder(respuestas);
  const faltaParaArmar = preguntasQueFaltan > 0
    ? preguntasQueFaltan === 1
      ? 'Responde la pregunta de arriba para armar tu marca.'
      : `Responde las ${preguntasQueFaltan} preguntas de arriba para armar tu marca.`
    : !borrador.nombre.trim()
    ? 'Escribe el nombre de tu marca para armarla.'
    : !borrador.rubro
      ? 'Elige tu rubro para armar tu marca.'
      : esOtro && !rubroDetalle.trim()
        ? 'Escribe cuál es tu rubro para armar tu marca.'
      : !contanos.descripcion.trim()
        ? 'Cuéntanos qué vendes, en una línea.'
        : null;

  /** "Armar mi marca": el mismo análisis que con un link, por su rama manual. */
  const armar = async () => {
    if (!companyId || faltaParaArmar || armando) return;
    setArmando(true);
    setError(null);
    // Lo que escribió la persona manda: el análisis no lo pisa.
    editados.current.add('nombre');
    editados.current.add('rubro');
    const r = await iniciarEscaneoManual({
      nombre: borrador.nombre.trim(),
      // Con "Otro", el análisis trabaja con lo que escribió: "Otro" solo no le dice nada.
      rubro: esOtro ? rubroQueCorresponde(rubroDetalle) ?? rubroDetalle.trim() : borrador.rubro,
      logoUrl: borrador.logoUrl,
      descripcion: contanos.descripcion.trim(),
      publico: contanos.publico.trim(),
    }, { marcaNueva: nueva });
    setArmando(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    guardarEstado({ companyId, escaneoId: r.escaneoId });
    ultimoRevelado.current = null;
    primeraLectura.current = false;
    setEscaneoId(r.escaneoId);
  };

  const terminado = puedeSeguir(revelados);
  const faltaRubro = terminado && !borrador.rubro;
  const falta = porQueNoSigue({
    terminado,
    respuestas,
    nombre: borrador.nombre,
    rubro: borrador.rubro,
    rubroDetalle,
    sinPreguntas,
  });
  // Una marca nueva cuyo análisis falló no tiene fila: confirmar la crea con lo revisado.
  const listoParaSeguir = terminado && (Boolean(borrador.id) || (nueva && escaneo.estado === 'fallo')) && !falta;

  const seguir = async () => {
    if (!companyId || !listoParaSeguir || guardando) return;
    setGuardando(true);
    setError(null);
    // En "describir" la fila es la default (la del escaneo): `confirmarMarcaNueva` la actualiza
    // por id y la deja activa; nunca crea una, porque sin `borrador.id` no se llega acá.
    if (nueva || (describir && borrador.id)) {
      const r = await confirmarMarcaNueva({
        companyId,
        marca: {
          id: borrador.id,
          nombre: borrador.nombre,
          rubro: rubroFinal(borrador.rubro, rubroDetalle),
          colores: borrador.colores,
          logoUrl: borrador.logoUrl,
          tipografias: borrador.tipografias,
          analisis: borrador.analisis,
          ...(detalleParaGuardar ? { rubroDetalle: detalleParaGuardar } : {}),
        },
      });
      if (!r.ok) {
        setError(r.error);
        setGuardando(false);
        return;
      }
      guardarEstado({ companyId, brandId: r.brandId });
      // Elementos de marca arranca con su logo y su tipografía; corre de fondo
      // (ADR-2026-10-22-los-elementos-de-marca-arrancan-con-lo-de-la-marca).
      void prepararElementosDeLaMarca({ companyId, brandId: r.brandId });
      navigate(describir ? DESCRIBIR_MARCA.lista : MARCA_NUEVA.lista);
      return;
    }
    if (!borrador.id) return;
    const r = await confirmarMarca({
      companyId,
      marca: {
        id: borrador.id,
        nombre: borrador.nombre,
        // Si eligió "Otro" pero lo que escribió es de la lista, se guarda ése: el rubro decide
        // qué modos ve (ADR-2026-10-12-el-rubro-otro-se-cuenta-y-se-ajusta).
        rubro: rubroFinal(borrador.rubro, rubroDetalle),
        colores: borrador.colores,
        logoUrl: borrador.logoUrl,
      },
      respuestas,
      ...(detalleParaGuardar ? { rubroDetalle: detalleParaGuardar } : {}),
    });
    if (!r.ok) {
      setError(r.error);
      setGuardando(false);
      return;
    }
    guardarEstado({ companyId, brandId: borrador.id });
    void prepararElementosDeLaMarca({ companyId, brandId: borrador.id });
    // Si vende por una plataforma, conectarla es lo siguiente; si no, las primeras piezas
    // (ADR-2026-10-02-onboarding-v2-canal-despues-de-la-marca).
    navigate(pasoTrasLaMarca(respuestas));
  };

  const dominio = inicial?.link ? dominioDe(inicial.link) : '';
  // Un título hecho sólo de entidades o espacios (el de zara.com es `&nbsp;`) no se muestra.
  const tituloDelSitio = !manual && (h.sitio?.titulo ?? '').replace(/&[a-z0-9#]+;/gi, ' ').trim() ? h.sitio?.titulo : null;
  const nombre = borrador.nombre.trim();

  // ── Cumbre ──
  const [quieto] = useState(pideMenosMovimiento);
  const estado = estadoDelTablero({ contando, terminado, fallo: escaneo.estado === 'fallo', demorado });
  // El nombre del globo es el primero que hay con el análisis terminado: si la persona lo
  // edita después, Cumbre no reescribe todo. Se espera a tenerlo (llega un instante
  // después que el "listo"); antes de que Cumbre arranque a escribir, no se nota.
  const nombreAlTerminar = useRef<string | null>(null);
  if ((estado === 'listo' || estado === 'incompleto') && !nombreAlTerminar.current && nombre) {
    nombreAlTerminar.current = nombre;
  }
  const nombreDelGlobo = nombreAlTerminar.current ?? '';
  const tramos = useMemo(
    () => dialogoDelTablero({ estado, manual, nombre: nombreDelGlobo }),
    [estado, manual, nombreDelGlobo],
  );
  const [yaHablo, setYaHablo] = useState(false);
  const dialogo = useDialogo(tramos, { instantaneo: quieto, arrancarEn: yaHablo ? 300 : 900 });
  // El contenido espera a que Cumbre termine la primera vez; después no se esconde más,
  // aunque Cumbre vuelva a hablar (al terminar el análisis).
  const [mostrar, setMostrar] = useState(quieto);
  useEffect(() => {
    if (!dialogo.terminado) return;
    setMostrar(true);
    setYaHablo(true);
  }, [dialogo.terminado]);

  // Terminó mientras miraba: festeja; si quedó incompleto, duda.
  const [gesto, setGesto] = useState<Gesto | null>(null);
  const estadoPrevio = useRef(estado);
  useEffect(() => {
    if (estadoPrevio.current === 'analizando' && estado !== 'analizando') {
      setGesto((g) => ({ tipo: estado === 'listo' ? 'festeja' : 'duda', n: (g?.n ?? 0) + 1 }));
    }
    estadoPrevio.current = estado;
  }, [estado]);

  const secciones = seccionesDelAnalisis(borrador.analisis);
  const tono = secciones[4] ? primerasOraciones(secciones[4], 2) : '';
  const publico = secciones[2] ? primerasOraciones(secciones[2], 1) : '';
  const muestraCaptura = !manual && visto('captura') && Boolean(h.captura);

  // Lo que falta, todo junto (no una carga por tarjeta).
  const pendientes = contando
    ? []
    : pasosPendientes(ordenPasos, revelados).map((id) => ({ id, etiqueta: etiquetaDelPaso(id, origen) }));

  // Con todo de una, cada sección entra en su turno de la cascada.
  let turno = 0;
  const orden = () => (llegoCompleto ? turno++ : undefined);

  const avisoDeLaBarra = error ? (
    <p role="alert" className="om-barra__aviso text-[#991B1B]">
      {error}
    </p>
  ) : contando ? (
    faltaParaArmar && preguntasQueFaltan > 0 ? (
      <button
        type="button"
        onClick={() => document.getElementById('om-preguntas')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        className="om-barra__aviso text-left text-[#92400E] underline-offset-2 hover:underline md:text-right"
      >
        {faltaParaArmar}
      </button>
    ) : faltaParaArmar ? (
      <p className="om-barra__aviso">{faltaParaArmar}</p>
    ) : null
  ) : falta?.motivo === 'preguntas' ? (
    <button
      type="button"
      onClick={() => document.getElementById('om-preguntas')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      className="om-barra__aviso text-left text-[#92400E] underline-offset-2 hover:underline md:text-right"
    >
      {falta.texto}
    </button>
  ) : falta ? (
    <p className="om-barra__aviso text-[#92400E]">{falta.texto}</p>
  ) : !terminado ? (
    <p className="om-barra__aviso">Se habilita cuando termine el análisis.</p>
  ) : null;

  return (
    <div className="relative isolate flex min-h-[calc(100dvh-var(--om-barra))] flex-col">
      <FondoCordillera quieto={quieto} />

      <div className="mx-auto flex w-full max-w-[980px] flex-col gap-5 px-4 pb-2 pt-6 md:gap-6 md:pb-4 md:pt-8">
        <div className="max-w-[680px]">
          <GuiaCumbre
            tramos={tramos}
            letras={dialogo.letras}
            hablando={dialogo.hablando}
            terminado={dialogo.terminado}
            onCompletar={dialogo.completar}
            reaccion={null}
            animo={armando || guardando ? 'piensa' : 'escucha'}
            gesto={gesto}
            instantaneo={quieto}
          />
        </div>

        {mostrar && (
          <>
            {/* Lo que falta analizar, todo junto y arriba de todo, debajo de Cumbre: lo primero
                que se lee es qué sigue en camino. */}
            {pendientes.length > 0 && (
              <AnalisisEnCurso
                pendientes={pendientes}
                listos={ordenPasos.length - pendientes.length}
                total={ordenPasos.length}
                manual={manual}
              />
            )}

            {/* Las preguntas tienen su paso, antes del tablero (ADR-2026-10-02-onboarding-v2-
                preguntas-con-cumbre). Acá quedan sólo de respaldo, si llegó sin responderlas:
                son obligatorias, y arriba, que al fondo nadie las ve. */}
            {preguntasQueFaltan > 0 && (
              <PreguntasDeEspera respuestas={respuestas} onResponder={responder} analizando={!contando && !terminado} />
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Tu marca: nombre, rubro y lo que se vio del sitio */}
              {(manual || visto('sitio')) && (
                <SeccionDeMarca
                  titulo={manual ? 'Tu marca' : 'Tu sitio'}
                  icono={manual ? 'storefront' : 'language'}
                  nota={dominio || undefined}
                  orden={orden()}
                  className="md:col-span-2"
                >
                  <div className={`grid gap-4 ${muestraCaptura ? 'sm:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]' : ''}`}>
                    <div className="flex min-w-0 flex-col gap-3">
                      {tituloDelSitio && <p className="om-cita">{tituloDelSitio}</p>}
                      <label className="om-etiqueta">
                        Nombre de la marca
                        <input
                          id="om-nombre"
                          value={borrador.nombre}
                          onChange={(e) => editar({ nombre: e.target.value })}
                          placeholder="Ej: Ciudad de Muebles"
                          className="om-input om-input--grande"
                        />
                      </label>
                      <label className="om-etiqueta">
                        Rubro
                        <select
                          id="om-rubro"
                          value={borrador.rubro}
                          onChange={(e) => editar({ rubro: e.target.value })}
                          className={`om-input ${faltaRubro ? 'is-falta' : ''}`}
                        >
                          <option value="" disabled>
                            {visto('colores') || manual ? 'Elige tu rubro' : 'Detectando…'}
                          </option>
                          {RUBROS.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </label>
                      {/* "Otro": cuál es. Si lo que escribe es un rubro de la lista, se le sugiere
                          (y al confirmar se usa ése): el rubro decide qué modos ve
                          (ADR-2026-10-12-el-rubro-otro-se-cuenta-y-se-ajusta). */}
                      {esOtro && (
                        <div className="flex flex-col gap-2">
                          <label className="om-etiqueta">
                            ¿Cuál es tu rubro?
                            <input
                              id="om-rubro-otro"
                              value={rubroDetalle}
                              onChange={(e) => setRubroDetalle(e.target.value)}
                              placeholder="Ej: mascotas, ferretería, regalos empresariales"
                              maxLength={80}
                              className={`om-input ${faltaRubro || (terminado && !rubroDetalle.trim()) ? 'is-falta' : ''}`}
                            />
                          </label>
                          {rubroSugerido && (
                            <p className="om-rubro-sugerido">
                              <span>
                                Eso entra en <strong>{rubroSugerido}</strong>.
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setDetalleDelSugerido({ rubro: rubroSugerido, detalle: rubroDetalle.trim() });
                                  editar({ rubro: rubroSugerido });
                                }}
                              >
                                Usar ese rubro
                              </button>
                            </p>
                          )}
                        </div>
                      )}
                      {contando && (
                        <>
                          <label className="om-etiqueta">
                            ¿Qué vendes?
                            <textarea
                              id="om-descripcion"
                              rows={2}
                              value={contanos.descripcion}
                              onChange={(e) => setContanos((c) => ({ ...c, descripcion: e.target.value }))}
                              placeholder="Ej: Camisas de lino y básicos de hombre, hechos en Argentina"
                              className="om-input resize-none"
                            />
                          </label>
                          <label className="om-etiqueta">
                            <span>
                              ¿A quién le vendes? <span className="font-normal text-[#98A1C0]">(opcional)</span>
                            </span>
                            <input
                              id="om-publico"
                              value={contanos.publico}
                              onChange={(e) => setContanos((c) => ({ ...c, publico: e.target.value }))}
                              placeholder="Ej: Hombres de 25 a 40 que compran online"
                              className="om-input"
                            />
                          </label>
                        </>
                      )}
                    </div>
                    {muestraCaptura && (
                      <figure className="om-navegador">
                        <div className="om-navegador__barra" aria-hidden="true">
                          <span />
                          <span />
                          <span />
                          <span className="om-navegador__url">{dominio}</span>
                        </div>
                        <img src={h.captura} alt={`Captura de ${dominio}`} className="om-navegador__captura" />
                      </figure>
                    )}
                  </div>
                </SeccionDeMarca>
              )}

              {/* Logo */}
              {(contando || visto('logo')) && (
                <SeccionDeMarca
                  titulo="Logo"
                  icono="verified"
                  orden={orden()}
                  falta={
                    borrador.logoUrl
                      ? null
                      : contando
                        ? 'Si tienes logo, súbelo: con sus colores armo tu paleta.'
                        : manual
                          ? null
                          : 'No se encontró un logo en tu sitio.'
                  }
                  acciones={
                    borrador.logoUrl ? (
                      <BotonDeSeccion onClick={() => inputLogo.current?.click()}>
                        {subiendoLogo ? 'Subiendo…' : 'Cambiar'}
                      </BotonDeSeccion>
                    ) : undefined
                  }
                >
                  {borrador.logoUrl ? (
                    <div className="om-damero om-logo">
                      <img src={borrador.logoUrl} alt={`Logo de ${nombre || 'tu marca'}`} className="max-h-[92px] max-w-full object-contain" />
                    </div>
                  ) : (
                    // Sin logo, la card entera invita a subirlo: un botón grande en el medio.
                    <button
                      type="button"
                      onClick={() => inputLogo.current?.click()}
                      disabled={subiendoLogo}
                      className="om-logo om-logo--subir"
                    >
                      <span className="om-logo__boton">
                        <span className="material-symbols-rounded" aria-hidden="true">
                          {subiendoLogo ? 'progress_activity' : 'upload'}
                        </span>
                        {subiendoLogo ? 'Subiendo…' : 'Subir logo'}
                      </span>
                      <span className="om-logo__formatos">PNG, JPG o WEBP</span>
                    </button>
                  )}
                  <input ref={inputLogo} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={alSubirLogo} />
                </SeccionDeMarca>
              )}

              {/* Colores */}
              {!contando && visto('colores') && (
                <SeccionDeMarca
                  titulo="Colores"
                  icono="palette"
                  orden={orden()}
                  className="md:col-span-2"
                  falta={borrador.colores.length || editandoColores ? null : 'No se pudieron detectar tus colores. Se pueden agregar aquí para que las piezas los usen.'}
                  acciones={
                    <BotonDeSeccion onClick={() => setEditandoColores((v) => !v)}>
                      {editandoColores ? 'Listo' : borrador.colores.length ? 'Editar' : 'Agregar colores'}
                    </BotonDeSeccion>
                  }
                >
                  {borrador.colores.length > 0 && (
                    <div className="om-paleta" aria-hidden="true">
                      {borrador.colores.map((c, i) => (
                        <span key={`${i}-${c.hex}`} style={{ background: c.hex }} />
                      ))}
                    </div>
                  )}
                  {(borrador.colores.length > 0 || editandoColores) && (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                      {borrador.colores.map((c, i) => (
                        <div key={`${i}-${c.role}`} className="om-muestra-color" style={{ ['--i' as string]: i }}>
                          <label className="om-muestra-color__circulo" style={{ background: c.hex }}>
                            <span className="sr-only">Cambiar {c.name || c.hex}</span>
                            {editandoColores && (
                              <>
                                <span className="om-muestra-color__lapiz material-symbols-rounded" aria-hidden="true">
                                  edit
                                </span>
                                <input
                                  type="color"
                                  value={/^#[0-9a-f]{6}$/i.test(c.hex) ? c.hex : '#000000'}
                                  onChange={(e) => cambiarColor(i, { hex: e.target.value.toUpperCase() })}
                                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                />
                              </>
                            )}
                          </label>
                          <span className="om-muestra-color__datos">
                          <span className="truncate text-[13px] font-bold leading-tight text-[#02062B]">{c.name || 'Color'}</span>
                          <span className="text-[11.5px] font-semibold tabular-nums text-[#6B7398]">{c.hex.toUpperCase()}</span>
                          {ETIQUETA_DE_ROL[c.role ?? ''] && <span className="om-muestra-color__rol">{ETIQUETA_DE_ROL[c.role ?? '']}</span>}
                          {editandoColores && (
                            <button
                              type="button"
                              onClick={() => editar({ colores: borrador.colores.filter((_, j) => j !== i) })}
                              className="self-start text-[11.5px] font-semibold text-[#B91C1C] hover:underline"
                            >
                              Quitar
                            </button>
                          )}
                          </span>
                        </div>
                      ))}
                      {editandoColores && borrador.colores.length < 5 && (
                        <button
                          type="button"
                          onClick={() =>
                            editar({
                              colores: [
                                ...borrador.colores,
                                { hex: '#1644EB', name: 'Nuevo color', role: borrador.colores.length === 0 ? 'primary' : 'accent' },
                              ],
                            })
                          }
                          className="om-muestra-color__sumar"
                        >
                          <span className="material-symbols-rounded" aria-hidden="true">
                            add
                          </span>
                          Agregar
                        </button>
                      )}
                    </div>
                  )}
                </SeccionDeMarca>
              )}

              {/* Tipografía */}
              {!contando && visto('tipografia') && (
                <SeccionDeMarca
                  titulo="Tipografía"
                  icono="text_fields"
                  orden={orden()}
                  nota={fuente ?? undefined}
                  falta={fuente || borrador.tipografias.style ? null : 'Se define con tus primeras piezas.'}
                >
                  {(fuente || borrador.tipografias.style) && (
                    <div className="om-tipo">
                      <span className="om-tipo__aa" style={fuente ? { fontFamily: `'${fuente}', sans-serif` } : undefined}>
                        Aa
                      </span>
                      <span className="om-tipo__nombre" style={fuente ? { fontFamily: `'${fuente}', sans-serif` } : undefined}>
                        {nombre || 'Tu marca'}
                      </span>
                      {borrador.tipografias.style && <span className="text-[12.5px] leading-snug text-[#4B5275]">{borrador.tipografias.style}</span>}
                    </div>
                  )}
                </SeccionDeMarca>
              )}

              {/* Forma de comunicar */}
              {!contando && visto('tono') && (
                <SeccionDeMarca
                  titulo="Forma de comunicar"
                  icono="record_voice_over"
                  orden={orden()}
                  className="md:col-span-3"
                  nota={tono || publico ? (manual ? 'A partir de tus datos' : 'A partir de tu sitio') : undefined}
                  falta={tono || publico ? null : 'Se va ajustando a medida que se crean piezas.'}
                >
                  {(tono || publico) && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {tono && (
                        <div className="om-burbuja">
                          <p className="om-burbuja__titulo">Tono</p>
                          <p>{tono}</p>
                        </div>
                      )}
                      {publico && (
                        <div className="om-burbuja om-burbuja--cian">
                          <p className="om-burbuja__titulo">Público</p>
                          <p>{publico}</p>
                        </div>
                      )}
                    </div>
                  )}
                </SeccionDeMarca>
              )}
            </div>

          </>
        )}
      </div>

      {mostrar && (
        <div className="om-barra">
          {/* En el teléfono, el botón primero y el aviso debajo; en desktop, el aviso a su izquierda. */}
          <div className="om-barra__dentro">
            {contando ? (
              <button
                type="button"
                onClick={() => void armar()}
                disabled={Boolean(faltaParaArmar) || armando || subiendoLogo}
                className={`om-boton md:order-last md:w-auto md:px-10 ${armando ? 'is-enviando om-esqueleto-azul' : ''}`}
              >
                {armando ? 'Empezando…' : 'Armar mi marca'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void seguir()}
                disabled={!listoParaSeguir || guardando}
                className={`om-boton md:order-last md:w-auto md:px-10 ${guardando ? 'is-enviando om-esqueleto-azul' : ''}`}
              >
                {guardando ? 'Guardando…' : terminado ? 'Todo listo, sigamos' : 'Seguir'}
              </button>
            )}
            {avisoDeLaBarra}
          </div>
        </div>
      )}
    </div>
  );
}

function pideMenosMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export default TableroDeMarca;
